/**
 * MooTools-free DOM element builder and alert/prompt creators.
 * Extracted from Shared/utils.js.
 */
import { _js } from '@ifixit/utils/localize';
import { faLegacyIconSvg } from 'Shared/faLegacyIcon';

/**
 * Parse a MooTools-style tag string like 'i.fa.fa-times.right'
 * into a tag name and class list.
 */
function parseTagString(tagString: string): { tag: string; classes: string[] } {
   const parts = tagString.split('.');
   return {
      tag: parts[0] || 'div',
      classes: parts.slice(1),
   };
}

/**
 * Apply MooTools-style properties to a native element.
 * Handles special keys (text, html, class, events) and falls back
 * to setAttribute for everything else — matching MooTools' behavior.
 */
function applyProps(el: HTMLElement, props: Record<string, any>): void {
   for (const [key, value] of Object.entries(props)) {
      switch (key) {
         case 'text': {
            el.textContent = String(value);
            break;
         }
         case 'html': {
            el.innerHTML = String(value);
            break;
         }
         case 'class': {
            if (value) {
               const existing = el.getAttribute('class') || '';
               el.setAttribute('class', existing ? existing + ' ' + value : value);
            }
            break;
         }
         case 'events': {
            for (const [eventName, handler] of Object.entries(
               value as Record<string, EventListenerOrEventListenerObject>
            )) {
               el.addEventListener(eventName, handler);
            }
            break;
         }
         default: {
            el.setAttribute(key, String(value));
            break;
         }
      }
   }
}

/**
 * Create a DOM element from a MooTools-style tag string and props.
 * Handles selector-style strings like 'div.foo.bar'.
 */
function createElement(tagString: string, props?: Record<string, any>): HTMLElement {
   const { tag, classes } = parseTagString(tagString);
   const el = document.createElement(tag);
   if (classes.length > 0) {
      el.setAttribute('class', classes.join(' '));
   }
   if (props) {
      applyProps(el, props);
   }
   return el;
}

interface ElementConfig {
   tag?: string;
   c?: string;
   children?: Array<string | Node | ElementConfig | false | null | undefined>;
   [key: string]: any;
}

/**
 * Recursively build a DOM tree from a config object.
 *
 * Accepts strings (→ text node), existing DOM nodes (→ returned as-is),
 * or config objects with `tag`, `c` (class shorthand), `children`,
 * and any other attributes.
 */
export function createElements(
   config: string | Node | ElementConfig | false | null | undefined
): Node {
   if (typeof config === 'string') {
      return document.createTextNode(config);
   }

   // Handle falsy values (e.g. `false` from `condition && element` in children arrays)
   if (!config) {
      return document.createTextNode('');
   }

   // If this is already a DOM element, just return it.
   if ('tagName' in config) {
      return config as Node;
   }

   const { tag, children, c, ...rest } = config as ElementConfig;
   if (c) {
      rest['class'] = c;
   }

   const el = createElement(tag || 'div', rest);

   if (children) {
      for (const child of children) {
         if (child != null && child !== false) {
            el.append(createElements(child));
         }
      }
   }

   return el;
}

/**
 * Creates an alert div and a close button. Add this to any element to
 * get a nicely styled alert.
 *
 * @param type - Alert type suffix (e.g. 'Error' → '.statusError.inPage')
 * @param contents - A string or an element to be placed in the alert.
 */
export function createAlert(type: string, contents: string | HTMLElement): HTMLElement {
   return createAlertPrompt(type, contents);
}

/**
 * Creates and returns an alert div with an optional 'Yes / No' prompt.
 * Both links close the alert and call `callback(confirmed)`, passing
 * true for Yes and false for No.
 *
 * @param type - Alert type suffix (e.g. 'Error' → '.statusError.inPage')
 * @param contents - A string or an element to be placed in the alert.
 * @param callback - If provided, Yes/No links are added.
 */
export function createAlertPrompt(
   type: string,
   contents: string | HTMLElement,
   callback?: (confirmed: boolean) => void
): HTMLElement {
   if (type) {
      type = '.status' + type.charAt(0).toUpperCase() + type.slice(1) + '.inPage';
   }

   const alertElement = createElement('div.status' + type);
   const button = createElement('i.fa.fa-times.right.close-status.fa-svg', {
      html: faLegacyIconSvg('times'),
      events: {
         click: alertCloser(false),
      },
   });

   alertElement.dataset.text = String(contents);

   if (typeof contents === 'string') {
      alertElement.textContent = contents;
   } else {
      alertElement.append(contents);
   }

   if (callback) {
      const YES = createElement('a.yesno.xbuttonLink', {
         text: _js('Yes'),
         events: {
            click: alertCloser(true),
         },
      });
      const NO = createElement('a.yesno.xbuttonLink', {
         text: _js('No'),
         events: {
            click: alertCloser(false),
         },
      });

      alertElement.append(YES);
      alertElement.append(document.createTextNode('/'));
      alertElement.append(NO);
   }

   alertElement.prepend(button);
   return alertElement;

   function alertCloser(confirmed: boolean) {
      return function (ev?: Event) {
         if (ev) {
            ev.preventDefault();
            ev.stopPropagation();
         }
         alertElement.remove();
         if (callback) {
            callback(confirmed);
         }
      };
   }
}
